import type { ReactNode } from 'react';
import { useFeature } from '@/contexts/SubscriptionContext';
import type { FeatureName } from '@/types/subscription';
import { UpgradePrompt } from './UpgradePrompt';

interface FeatureGateProps {
  feature: FeatureName;
  children: ReactNode;
  /**
   * What to show when feature is locked
   * - 'upgrade': Show upgrade prompt (default)
   * - 'hidden': Hide completely
   * - ReactNode: Custom fallback
   */
  fallback?: 'upgrade' | 'hidden' | ReactNode;
  /**
   * Title for the upgrade prompt
   */
  upgradeTitle?: string;
  /**
   * Description for the upgrade prompt
   */
  upgradeDescription?: string;
}

export function FeatureGate({
  feature,
  children,
  fallback = 'upgrade',
  upgradeTitle,
  upgradeDescription,
}: FeatureGateProps) {
  const { allowed, upgradeRequired } = useFeature(feature);

  if (allowed) {
    return <>{children}</>;
  }

  // Hidden mode - render nothing
  if (fallback === 'hidden') {
    return null;
  }

  // Custom fallback
  if (fallback !== 'upgrade') {
    return <>{fallback}</>;
  }

  // Default upgrade prompt
  return (
    <UpgradePrompt
      feature={feature}
      requiredTier={upgradeRequired}
      title={upgradeTitle}
      description={upgradeDescription}
    />
  );
}

// Convenience component for wrapping nav items
interface FeatureNavItemProps {
  feature: FeatureName;
  children: ReactNode;
  showLockBadge?: boolean;
}

export function FeatureNavItem({
  feature,
  children,
  showLockBadge = true,
}: FeatureNavItemProps) {
  const { allowed } = useFeature(feature);

  if (allowed) {
    return <>{children}</>;
  }

  if (showLockBadge) {
    return (
      <div className="relative opacity-60 pointer-events-none">
        {children}
        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary-600 dark:bg-secondary-700">
          <svg
            className="h-2.5 w-2.5 text-secondary-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </span>
      </div>
    );
  }

  return null;
}

// HOC for feature-gating entire pages
export function withFeatureGate<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  feature: FeatureName,
  options?: Omit<FeatureGateProps, 'feature' | 'children'>
) {
  return function FeatureGatedComponent(props: P) {
    return (
      <FeatureGate feature={feature} {...options}>
        <WrappedComponent {...props} />
      </FeatureGate>
    );
  };
}
